import React, { useState, useEffect } from 'react';
import { FaChild, FaPlus, FaEdit, FaTrash, FaArrowLeft, FaCrown } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import { validateForm } from '../../utils/validators';
import SubscriptionAlert from '../dashboard/SubscriptionAlert';
import { hasSubscriptionFeature } from '../../utils/helpers';

/**
 * Child management component for parents
 * Allows adding, editing, and deleting children's information
 */
const ChildManagement = () => {
  const location = useLocation();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();

  // Child states
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    school: '',
    grade: '',
    interests: '',
    notes: ''
  });
  
  // Form errors
  const [formErrors, setFormErrors] = useState({});

  // Modify the hasActiveSubscription check to also consider the free tier
  const hasActiveSubscription = user && 
    user.subscription && 
    user.subscription.isActive && 
    user.subscription.tier !== 'free';  // Free tier is considered "active" but can't add children
    
  // Get child limit based on subscription tier
  const getMaxChildrenAllowed = () => {
    if (!hasActiveSubscription) return 0;
    
    const childProfiles = hasSubscriptionFeature(user, 'childProfiles');
    return childProfiles === "unlimited" ? Infinity : childProfiles;
  };
  
  // Check if user can add more children
  const canAddMoreChildren = () => {
    const maxChildren = getMaxChildrenAllowed();
    return children.length < maxChildren;
  };

  // Fetch children on component mount
  useEffect(() => {
    fetchChildren();
    
    // Check for edit parameter in URL
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');
    if (editId) {
      handleEdit(editId);
    }
  }, [location]);

  // Fetch children from API
  const fetchChildren = async () => {
    try {
      setLoading(true);
      const response = await api.get('/parents/children');
      setChildren(response.data.data || []);
    } catch (error) {
      console.error('Error fetching children:', error);
      showError('Failed to load children');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear the error for this field if there was one
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Open add form
  const handleAddNew = () => {
    // First check if user has an active subscription at all
    if (!user.subscription || !user.subscription.isActive) {
      setSubscriptionRequired(true);
      showError("An active subscription is required to manage children.");
      return;
    }
    
    // Now check if it's a free tier (cannot add children)
    if (user.subscription.tier === 'free') {
      setSubscriptionRequired(true);
      showError("Free tier does not allow adding children. Please upgrade your subscription.");
      return;
    }
    
    // Check if user has reached their child limit
    const maxChildren = getMaxChildrenAllowed();
    if (children.length >= maxChildren) {
      setSubscriptionRequired(true);
      showError(`You have reached the maximum number of children (${maxChildren}) allowed for your ${user.subscription.tier} subscription. Please upgrade your plan for more.`);
      return;
    }
    
    // If all checks pass, show the form
    setFormData({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      school: '',
      grade: '',
      interests: '',
      notes: ''
    });
    setFormMode('add');
    setShowForm(true);
    setFormErrors({});
  };

  // Open edit form
  const handleEdit = (childId) => {
    // Check if subscription is active before proceeding
    if (!hasActiveSubscription) {
      setSubscriptionRequired(true);
      return;
    }
    
    const child = typeof childId === 'string' 
      ? children.find(c => c._id === childId)
      : childId;
      
    if (!child) return;
    
    setSelectedChild(child);
    setFormData({
      firstName: child.firstName || '',
      lastName: child.lastName || '',
      dateOfBirth: child.dateOfBirth ? new Date(child.dateOfBirth).toISOString().split('T')[0] : '',
      school: child.school || '',
      grade: child.grade || '',
      interests: child.interests || '',
      notes: child.notes || ''
    });
    setFormMode('edit');
    setShowForm(true);
    setFormErrors({});
  };

  // Open delete confirmation
  const handleDeleteClick = (child) => {
    // Check if subscription is active before proceeding
    if (!hasActiveSubscription) {
      setSubscriptionRequired(true);
      return;
    }
    
    setSelectedChild(child);
    setShowDeleteModal(true);
  };

  // Delete child
  const handleDeleteConfirm = async () => {
    if (!selectedChild) return;
    
    try {
      await api.delete(`/parents/children/${selectedChild._id}`);
      showSuccess('Child deleted successfully');
      setShowDeleteModal(false);
      fetchChildren();
    } catch (error) {
      console.error('Error deleting child:', error);
      if (error.response?.status === 403 && error.response?.data?.error?.includes('subscription')) {
        setSubscriptionRequired(true);
        showError('Active subscription required to manage children');
      } else {
        showError('Failed to delete child');
      }
    }
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationRules = {
      firstName: { required: true, requiredMessage: 'First name is required' },
      lastName: { required: true, requiredMessage: 'Last name is required' },
      dateOfBirth: { required: true, requiredMessage: 'Date of birth is required' }
    };
    
    const { isValid, errors } = validateForm(formData, validationRules);
    
    if (!isValid) {
      setFormErrors(errors);
      return;
    }
    
    try {
      if (formMode === 'add') {
        await api.post('/parents/children', formData);
        showSuccess('Child added successfully');
      } else {
        await api.put(`/parents/children/${selectedChild._id}`, formData);
        showSuccess('Child updated successfully');
      }
      
      setShowForm(false);
      fetchChildren();
    } catch (error) {
      console.error('Error saving child:', error);
      if (error.response?.status === 403 && error.response?.data?.error?.includes('subscription')) {
        setSubscriptionRequired(true);
        showError(error.response?.data?.error || 'Active subscription required to manage children');
      } else {
        showError(error.response?.data?.message || 'Failed to save child');
      }
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    
    return age;
  };

  // Generate subscription message based on current tier
  const getSubscriptionMessage = () => {
    if (!hasActiveSubscription) {
      return "An active subscription is required to manage children. Please subscribe to unlock this feature.";
    }
    
    const tier = user.subscription.tier;
    const maxChildren = getMaxChildrenAllowed();
    
    if (tier === 'free') {
      return "The free tier does not allow adding children. Please upgrade to Essential, Growth, or Plus to add children.";
    }
    
    if (children.length >= maxChildren) {
      if (tier === 'essential') {
        return "The Essential plan allows only 1 child. Please upgrade to Growth (2 children) or Plus (unlimited) for more.";
      } else if (tier === 'growth') {
        return "The Growth plan allows only 2 children. Please upgrade to Plus for unlimited children.";
      }
    }
    
    return "Please upgrade your subscription to manage more children.";
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaChild className="mr-2 text-primary" /> Child Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage your children's information
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <button
            onClick={handleAddNew}
            className="btn-primary flex items-center"
          >
            <FaPlus className="mr-2" /> Add New Child
          </button>
        </div>
      </div>
      
      {/* Subscription Alert */}
      <SubscriptionAlert />
      
      {/* Subscription Required Alert */}
      {subscriptionRequired && (
        <div className="mb-6 p-4 border rounded-md bg-yellow-50 text-yellow-800 border-yellow-200">
          <div className="flex items-start">
            <div className="mr-3 mt-0.5 text-yellow-500">
              <FaCrown className="text-xl" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold mb-1">Subscription Required</h3>
              <p className="text-sm mb-3">{getSubscriptionMessage()}</p>
              <Link 
                to="/parent/subscription" 
                className="inline-block px-4 py-2 text-sm text-white rounded bg-yellow-500 hover:bg-yellow-600 transition-colors"
              >
                View Subscription Plans
              </Link>
            </div>
          </div>
        </div>
      )}
      
      {/* Children List */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-8 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft p-6">
          {children.length === 0 ? (
            <div className="text-center py-12">
              <FaChild className="mx-auto text-gray-300 text-5xl mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">No Children Added Yet</h3>
              <p className="text-gray-500 mb-6">Add your child to track their progress and manage their education</p>
              <button
                onClick={handleAddNew}
                className="btn-primary flex items-center mx-auto"
              >
                <FaPlus className="mr-2" /> Add Child
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {children.map((child) => (
                <div 
                  key={child._id} 
                  className="border border-gray-200 rounded-lg hover:border-primary transition-colors p-6"
                >
                  <div className="flex items-center mb-4">
                    {child.profilePicture ? (
                      <img 
                        src={child.profilePicture} 
                        alt={child.firstName} 
                        className="w-16 h-16 rounded-full object-cover mr-4"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mr-4">
                        <FaChild className="text-primary text-2xl" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-lg">
                        {child.firstName} {child.lastName}
                      </h3>
                      <p className="text-gray-500">
                        {calculateAge(child.dateOfBirth)} years old
                      </p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    {child.school && (
                      <p className="text-sm">
                        <span className="font-medium">School:</span> {child.school}
                      </p>
                    )}
                    {child.grade && (
                      <p className="text-sm">
                        <span className="font-medium">Grade:</span> {child.grade}
                      </p>
                    )}
                    {child.interests && (
                      <p className="text-sm">
                        <span className="font-medium">Interests:</span> {child.interests}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex justify-end space-x-2 mt-4">
                    <button
                      onClick={() => handleEdit(child)}
                      className="p-2 text-gray-600 hover:text-primary"
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(child)}
                      className="p-2 text-gray-600 hover:text-red-500"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Add/Edit Child Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {formMode === 'add' ? 'Add New Child' : 'Edit Child'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`form-input w-full ${formErrors.firstName ? 'border-red-500' : ''}`}
                  />
                  {formErrors.firstName && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.firstName}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`form-input w-full ${formErrors.lastName ? 'border-red-500' : ''}`}
                  />
                  {formErrors.lastName && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>
                  )}
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Date of Birth</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className={`form-input w-full ${formErrors.dateOfBirth ? 'border-red-500' : ''}`}
                />
                {formErrors.dateOfBirth && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.dateOfBirth}</p>
                )}
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">School (Optional)</label>
                <input
                  type="text"
                  name="school"
                  value={formData.school}
                  onChange={handleChange}
                  className="form-input w-full"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Grade (Optional)</label>
                <input
                  type="text"
                  name="grade"
                  value={formData.grade}
                  onChange={handleChange}
                  className="form-input w-full"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Interests (Optional)</label>
                <input
                  type="text"
                  name="interests"
                  value={formData.interests}
                  onChange={handleChange}
                  className="form-input w-full"
                  placeholder="e.g. Math, Reading, Sports"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Notes (Optional)</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="form-textarea w-full"
                  rows="3"
                ></textarea>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {formMode === 'add' ? 'Add Child' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Deletion"
        size="sm"
      >
        <div className="p-6">
          <p className="mb-4">
            Are you sure you want to delete {selectedChild?.firstName} {selectedChild?.lastName}?
            This action cannot be undone.
          </p>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="btn-danger"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ChildManagement;
